import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Collapse,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import CustomCheckbox from "../../../components/CustomCheckbox";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../components/CustomTextField";
import FieldInfoIcon, {
  FieldHelpLabel,
} from "../../../components/FieldInfoIcon";
import Select from "../../../components/Select/Select";
import {
  addUpdateUomMappingByStockId,
  createStocks,
  getClinicStocksWoLoader,
  getStockSalePurchaseHistory,
  updateStockWithBatchList,
} from "../../../redux/reducers/clinicSlice";
import { hideLoader, showLoader } from "../../../redux/reducers/loaderSlice";
import { isActiveRecord } from "../../../util/activeStatus";
import { AppColors } from "../../../util/AppColors";
import { drugScheduleShowCategoryList } from "../../../util/arrayList";
import { addIcon, delBtn, plusBtn } from "../../../util/CommonIconStyle";
import {
  administeredUOMList,
  dosageTypes,
  drugScheduleList,
} from "../../../util/dropList";
import {
  getExactNumber,
  getRightSaluation,
  matchStrIgnoreCase,
} from "../../../util/function";
import { patchMandateSession } from "../../../util/mandateSessionStorage";
import { tblGrayBack } from "../../../util/object";
import { STOCK_FIELD_HELP } from "../../../util/stockFieldHelp";
import TransactionHistory from "../../Hospital/Inventory/TransactionHistory";

const hl = (label, key) => (
  <FieldHelpLabel label={label} infoText={STOCK_FIELD_HELP[key]} />
);

const resolveStockLogPerformer = (createdBy, clinicVets = [], clinic = {}) => {
  if (createdBy == null || createdBy === "") return "-";
  const idStr = String(createdBy);

  const fromVet = (clinicVets ?? []).find(
    (v) =>
      String(v?.doctorId) === idStr ||
      String(v?.userId) === idStr ||
      String(v?.id) === idStr,
  );
  if (fromVet) {
    const salutation = getRightSaluation(
      clinicVets,
      fromVet?.doctorId ?? fromVet?.userId ?? fromVet?.id,
    );
    return `${salutation}${fromVet?.name || ""}`.trim() || "-";
  }

  if (
    String(clinic?.userId) === idStr ||
    String(clinic?.id) === idStr ||
    String(clinic?.createdBy) === idStr ||
    String(clinic?.ownerId) === idStr
  ) {
    return (
      clinic?.userName ||
      clinic?.ownerName ||
      clinic?.name ||
      clinic?.clinicName ||
      `User ${idStr}`
    );
  }

  return `User ${idStr}`;
};

const mapStockBatchEditLogs = (
  data = [],
  clinicId = null,
  clinicVets = [],
  clinic = {},
  batches = [],
) => {
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
      ? data.data
      : [];
  const resolveBatchNumber = (batchId) => {
    if (batchId == null || batchId === "") return null;
    const matched = (batches ?? []).find(
      (bat) => String(bat?.batchId) === String(batchId),
    );
    return matched?.batchNo || matched?.batchNumber || null;
  };
  return list
    .filter((item) => {
      if (clinicId == null || clinicId === "") return false;
      return String(item?.clinicId) === String(clinicId);
    })
    .map((item, index) => {
      const action =
        item?.type === "batchEdit"
          ? "Batch Edit"
          : item?.type || item?.action || "-";
      const changeParts = [];
      const batchNumber = resolveBatchNumber(item?.batchId);
      if (batchNumber) {
        changeParts.push(`Batch No: ${batchNumber}`);
      } else if (item?.batchId != null && item?.batchId !== "") {
        changeParts.push(`Batch Id: ${item.batchId}`);
      }
      if (item?.quantity != null && item?.quantity !== "") {
        changeParts.push(`Qty ${item.quantity}`);
      }
      if (item?.locationName) {
        changeParts.push(`Location: ${item.locationName}`);
      }
      if (item?.unitSellingPrice != null && item?.unitSellingPrice !== "") {
        changeParts.push(`USP ${Number(item.unitSellingPrice).toFixed(2)}`);
      }
      if (item?.landedCost != null && item?.landedCost !== "") {
        changeParts.push(`Landed Cost ${Number(item.landedCost).toFixed(2)}`);
      }
      return {
        key: `${item?.id ?? "log"}_${index}_${item?.createdDate ?? ""}`,
        createdDate: item?.createdDate || null,
        action,
        changes: changeParts.length ? changeParts.join(", ") : "-",
        performedBy: resolveStockLogPerformer(
          item?.createdBy,
          clinicVets,
          clinic,
        ),
      };
    });
};

const reqTxt = "This field is required";
const requiredFields = [
  "category",
  "subCategory",
  "name",
  "tradeName",
  "cgst",
  "sgst",
];
const requiredFieldsNonBillable = [
  "category",
  "subCategory",
  "name",
  "tradeName",
];
const batchRequiredFields = [
  "location",
  "quantity",
  // "subunits",
  // "unitSellingPrice",
  // "pricingUOM",
  "quantityUOM",
];
const initBatch = {
  batchNumber: "",
  manufacturingDate: null,
  expiryDate: null,
  location: null,
  quantity: "",
  subunits: "",
  unitSellingPrice: "",
  landedCost: "",
  quantityUOM: null,
  pricingUOM: null,
};
const defObj = {
  locations: [],
  clinic: {},
  dataSharing: {},
  stockCategories: [],
};

/** Session dropdowns: only explicitly active records (active: true / Y). */
const isSessionActiveRecord = (record) => isActiveRecord(record, false);

const toSubCategoryList = (subCategories) => {
  const list = Array.isArray(subCategories) ? subCategories : [];
  return list
    .filter((sc) => sc?.name && isSessionActiveRecord(sc))
    .map((sc) => ({
      name: sc.name,
      value: sc.name,
      id: sc.id,
    }));
};

const mapStockCategoriesFromSession = (categories) => {
  const list = Array.isArray(categories) ? categories : [];
  return list
    .filter(
      (cat) => cat?.type === "stock" && cat?.name && isSessionActiveRecord(cat),
    )
    .map((cat) => ({
      name: cat.name,
      value: cat.name,
      id: cat.id,
      subCategories: (
        Array.isArray(cat.subCategories)
          ? cat.subCategories
          : Array.isArray(cat.subCategory)
            ? cat.subCategory
            : []
      ).filter((sc) => sc?.name && isSessionActiveRecord(sc)),
    }));
};

const StockCreateUpdate = ({
  modVsble,
  selecetdStock,
  onCloseMod = () => {},
}) => {
  const dispatch = useDispatch();
  const [editValues, setEditValues] = useState({});
  const [editErrs, setEditErrs] = useState({});
  const [editHelps, setEditHelps] = useState({});
  const [stckErrs, setStckErrs] = useState({});
  const [batchValues, setBatchValues] = useState({});
  const [batchErrs, setBatchErrs] = useState({});
  const [batchHelps, setBatchHelps] = useState({});
  const [ledgerModVsble, setLedgerModVsble] = useState(false);
  const [batchEditLogs, setBatchEditLogs] = useState([]);
  const [changeLogOpen, setChangeLogOpen] = useState(false);
  const [changeLogFetched, setChangeLogFetched] = useState(false);
  const { stocksByFilter, branchesDataSharing } = useSelector(
    (state) => state?.clinic,
  );
  const clinicVets = useSelector((state) => state?.vet?.vets?.vets) ?? [];
  const user = localStorage.getItem("user");
  const profile = JSON.parse(user);
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const { locations, clinic, dataSharing, stockCategories } = useMemo(() => {
    if (!mandateDataRaw) return defObj;
    try {
      const reqData = JSON.parse(mandateDataRaw);
      const categories = Array.isArray(reqData?.categories)
        ? reqData.categories
        : [];
      return {
        locations: Array.isArray(reqData?.locations) ? reqData?.locations : [],
        clinic: reqData?.clinic ?? {},
        dataSharing: reqData?.dataSharing ?? {},
        stockCategories: mapStockCategoriesFromSession(categories),
      };
    } catch {
      return defObj;
    }
  }, [mandateDataRaw]);

  const subCategoryList = useMemo(() => {
    const selected = editValues?.category;
    if (!selected) return [];
    const sessionCategory =
      stockCategories?.find(
        (cat) =>
          (selected?.id != null && cat?.id === selected?.id) ||
          cat?.value === selected?.value ||
          cat?.name === selected?.name,
      ) ?? selected;
    return toSubCategoryList(sessionCategory?.subCategories);
  }, [editValues?.category, stockCategories]);

  const batchEditLogRows = useMemo(
    () =>
      mapStockBatchEditLogs(
        batchEditLogs,
        clinic?.id,
        clinicVets,
        clinic,
        selecetdStock?.stockBatch ??
          selecetdStock?.batches ??
          editValues?.batchList ??
          [],
      ),
    [
      batchEditLogs,
      clinic?.id,
      clinicVets,
      clinic,
      selecetdStock?.stockBatch,
      selecetdStock?.batches,
      editValues?.batchList,
    ],
  );

  useEffect(() => {
    setBatchEditLogs([]);
    setChangeLogOpen(false);
    setChangeLogFetched(false);
  }, [selecetdStock?.stockId, modVsble]);

  const handleToggleChangeLog = () => {
    const nextOpen = !changeLogOpen;
    setChangeLogOpen(nextOpen);
    const stockId = selecetdStock?.stockId;
    if (!nextOpen || !stockId || changeLogFetched) return;
    dispatch(getStockSalePurchaseHistory({ stockId, type: "batchEdit" })).then(
      (res) => {
        setBatchEditLogs(Array.isArray(res?.payload) ? res.payload : []);
        setChangeLogFetched(true);
      },
    );
  };

  useEffect(() => {
    if (selecetdStock) {
      appendStockValues();
    } else {
      const batchList = [
        // { ...initBatch, i: editValues?.batchList?.length ?? 0 },
      ];
      const uomMapList = [];
      setEditValues({
        uomMapList,
        batchList,
        activeBatchIndex: null, //0,
        isBatchEdit: false, //true
        billable: true,
        active: true,
        ndps: false,
        autoConsumed: false,
        landedCost: "0",
        purchaseUom: null,
        adminUom: administeredUOMList?.[0],
        category: null,
        subCategory: null,
        dosageType: null,
        location: null,
        drugSchedule: null,
        reorderLevel: "",
        salesUom: null,
      });
      setBatchValues(
        {},
        // { ...initBatch }
      );
    }
  }, [selecetdStock]);

  const appendStockValues = () => {
    const itm = selecetdStock;
    const clinicBatches = (itm?.stockBatch ?? itm?.batches)?.filter(
      (btch) => btch?.createdBy === clinic?.id,
    );
    const getClinicSettings = (itm?.clinic_stock_settings ?? [])?.find(
      (st) => st?.createdBy === clinic?.id,
    );
    const reqBatch = clinicBatches?.[0];
    const reqLocation = locations?.find(
      (loc) => loc?.id === reqBatch?.locationId,
    );
    const clinicUoms = (itm?.uomMappings ?? [])?.filter((ul) =>
      ul?.uomMappingId ? ul?.createdBy === clinic?.id : true,
    );
    const thresholdIdMatch = itm?.thresholdUomId
      ? clinicUoms?.find((li) => li?.uomMappingId === itm?.thresholdUomId)
      : null;
    const reqThresholdUom = itm?.thresholdUomName
      ? administeredUOMList?.find((li) =>
          matchStrIgnoreCase(li?.value, itm?.thresholdUomName ?? ""),
        )
      : thresholdIdMatch
        ? {
            name: thresholdIdMatch?.unit,
            value: thresholdIdMatch?.unit,
            uomMappingId: thresholdIdMatch?.uomMappingId,
          }
        : null;
    const reqUomMaps =
      clinicUoms?.length > 0
        ? [...(clinicUoms ?? [])]
            ?.sort((a, b) => {
              const aid = Number(a?.uomMappingId);
              const bid = Number(b?.uomMappingId);
              return (
                (Number.isFinite(aid) ? aid : 0) -
                (Number.isFinite(bid) ? bid : 0)
              );
            })
            ?.map((ul, i) => {
              const reqUnit = administeredUOMList?.find((al) =>
                matchStrIgnoreCase(al?.value, ul?.unit),
              );
              return {
                createdBy: ul?.createdBy,
                unit: reqUnit ?? null,
                conversion: ul?.conversion,
                allowedSale: ul?.allowSale === "Y",
                uomMappingId: ul?.uomMappingId,
                i,
                delete: false,
              };
            })
        : [];
    const reqBatchList = (clinicBatches ?? [])?.map((bat, i) => {
      const batLoc = locations?.find((loc) => loc?.id === bat?.locationId);
      const reqPricingUom =
        bat?.uomMapName?.length > 0
          ? administeredUOMList?.find((li) =>
              matchStrIgnoreCase(li?.value, bat?.uomMapName ?? ""),
            )
          : null;
      const reqQuantityUOM =
        bat?.qtyMapName?.length > 0
          ? administeredUOMList?.find((li) =>
              matchStrIgnoreCase(li?.value, bat?.qtyMapName ?? ""),
            )
          : null;
      const obj = {
        i,
        batchNumber: bat?.batchNo,
        batchId: bat?.batchId,
        manufacturingDate: bat?.mfgDate ?? null,
        expiryDate: bat?.expiryDate ?? null,
        location: {
          name: batLoc?.locationName,
          value: batLoc?.locationName,
          id: batLoc?.id ?? bat?.locationId,
        },
        quantity: bat?.quantity,
        subunits: bat?.subunits,
        unitSellingPrice: Number(bat?.unitSellingPrice)?.toFixed(2),
        landedCost: bat?.landedCost,
        createdBy: bat?.createdBy,
        pricingUOM: reqPricingUom,
        // bat?.uomMapName?.length > 0
        //   ? {
        //       name: (bat?.uomMapName ?? "")?.trim(),
        //       value: (bat?.uomMapName ?? "")?.trim(),
        //       uomMappingId: bat?.mapId ?? null,
        //     }
        //   : null,
        quantityUOM: reqQuantityUOM,
        // bat?.qtyMapName?.length > 0
        //   ? {
        //       name: (bat?.qtyMapName ?? "")?.trim(),
        //       value: (bat?.qtyMapName ?? "")?.trim(),
        //       uomMappingId: bat?.qtyMapId ?? null,
        //     }
        //   : null,
      };

      return obj;
    });
    const reqObj = {
      ...editValues,
      uomMapList: reqUomMaps,
      skuId: itm?.skuId ?? "",
      isBatchEdit: false,
      batchList: reqBatchList,
      active: getClinicSettings?.active ?? false, // itm?.active === "Y",
      ndps: itm?.ndps ?? false,
      autoConsumed: getClinicSettings?.autoConsumed ?? false, //itm?.autoConsumed
      billable: getClinicSettings?.billable ?? false, // itm?.billable,
      category:
        stockCategories?.find((cat) => cat?.value === itm?.category) ??
        (itm?.category ? { name: itm.category, value: itm.category } : null),
      subCategory: itm?.subCategory
        ? { name: itm.subCategory, value: itm.subCategory }
        : null,
      hsnSacCode: itm?.hsnSacCode,
      uqcCode: itm?.uqcCode ?? "",
      name: itm?.productName,
      tradeName: itm?.tradeName,
      shortName: itm?.shortName,
      ingredient: itm?.activeIngredient,
      nature: itm?.nature,
      code: itm?.barcodeNo,
      // code: reqBatch?.barcodeNo,
      brand: itm?.brand,
      description: itm?.description,
      batchNumber: reqBatch?.batchNo,
      batchId: reqBatch?.batchId,
      manufacturingDate: reqBatch?.mfgDate,
      expiryDate: reqBatch?.expiryDate,
      location: {
        name: reqLocation?.locationName,
        value: reqLocation?.locationName,
        id: reqLocation?.id ?? reqBatch?.locationId,
      },
      // purchaseUom: reqBatch?.purchaseUom ?? "",
      purchaseUom:
        administeredUOMList?.find((al) =>
          matchStrIgnoreCase(
            al?.value,
            itm?.purchaseUom ?? reqBatch?.purchaseUom,
          ),
        ) ?? null,
      // salesUom:
      //   administeredUOMList?.find((al) =>
      //     matchStrIgnoreCase(al?.value, itm?.salesUom ?? reqBatch?.salesUom),
      //   ) ?? null,
      conversionFactor: itm?.conversionFactor ?? reqBatch?.conversionFactor,
      quantity: reqBatch?.quantity,
      subunits: reqBatch?.subunits,
      cgst: itm?.cgst ?? reqBatch?.cgst,
      sgst: itm?.sgst ?? reqBatch?.sgst,
      itemTotal: Number(reqBatch?.itemTotal)?.toFixed(2),
      unitSellingPrice: Number(reqBatch?.unitSellingPrice)?.toFixed(2),
      oldSellingPrice: Number(reqBatch?.unitSellingPrice)?.toFixed(2),
      barcodeNo: itm?.barcodeNo,
      // barcodeNo: reqBatch?.barcodeNo,
      landedCost: Number(reqBatch?.landedCost)?.toFixed(2),
      dosage: itm?.dosage ?? reqBatch?.dosage ?? "",
      dosageType:
        dosageTypes?.find(
          (li) => li?.value === itm?.dosageType ?? reqBatch?.dosageType,
        ) ?? null,
      reorderLevel: itm?.reorderLevel ?? "",
      drugSchedule:
        drugScheduleList?.find((cat) => cat?.value === itm?.drugSchedule) ??
        null,
      adminUom: null,
      reorderQty: Number(getClinicSettings?.reorderQty ?? 0),
      thresholdUom: reqThresholdUom,
      groupName: itm?.groupName ?? "",
    };
    if (itm?.packageUom ?? reqBatch?.packageUom) {
      const reqPack = (itm?.packageUom ?? reqBatch?.packageUom ?? "")?.match(
        /^(\d+)(.+)/,
      );
      if (reqPack) {
        const reqAdminUom = administeredUOMList?.find((li) =>
          matchStrIgnoreCase(li?.value, reqPack?.[2]),
        );
        reqObj.packageUom = reqPack?.[1];
        reqObj.adminUom = reqAdminUom ?? null;
      } else {
        const reqAdminUom = administeredUOMList?.find((li) =>
          matchStrIgnoreCase(
            li?.value,
            itm?.packageUom ?? reqBatch?.packageUom,
          ),
        );
        reqObj.packageUom = "";
        reqObj.adminUom = reqAdminUom ?? null;
      }
    }
    setEditValues(reqObj);
  };

  const onCloseEditMod = (typ) => {
    onCloseMod(typ);
  };

  const handleChange = (name, value, addObj) => {
    let reqObj = { ...editValues, [name]: value };
    const reqValue = typeof value === "object" ? value?.value : value;
    if (name === "batchNumber") reqObj.batchId = addObj?.batchId;
    if (name === "sgst" || name === "cgst") {
      reqObj.cgst = value;
      reqObj.sgst = value;
      reqObj.gst = Number(value) * 2;
      reqObj.igst = Number(value) * 2;
    }
    if (name === "conversionFactor") {
      reqObj.subunits = Number(value) * Number(editValues?.quantity);
      // reqObj.unitSellingPrice =
      //   Number(reqObj?.oldSellingPrice ?? reqObj?.unitSellingPrice ?? 0) /
      //   Number(value);
      if (!selecetdStock) {
        reqObj.landedCost =
          Number(editValues?.itemTotal ?? 0) /
          (Number(value ?? 0) * Number(editValues?.quantity ?? 0));
      }
    }
    if (name === "quantity") {
      reqObj.subunits =
        Number(editValues?.conversionFactor ?? 1) * Number(value);
      // reqObj.unitSellingPrice =
      //   Number(reqObj?.oldSellingPrice ?? reqObj?.unitSellingPrice ?? 0) /
      //   Number(value);
      if (!selecetdStock) {
        reqObj.landedCost =
          Number(editValues?.itemTotal ?? 0) /
          (Number(editValues?.conversionFactor ?? 1) * Number(value ?? 0));
      }
    }
    if (name === "unitSellingPrice") {
      reqObj.oldSellingPrice = value;
    }
    if (name === "batchNumber") {
      if (value?.value || addObj?.batchId) {
        const reqBatch = stocksByFilter?.data
          ?.find((stc) => stc?.stockId === selecetdStock?.stockId)
          ?.[
            "stockBatch" || "batches"
          ]?.find((bat) => bat?.batchId === value?.value || addObj?.batchId);
        const reqLocation = locations?.find(
          (loc) => loc?.id === reqBatch?.locationId,
        );
        reqObj = {
          ...reqObj,
          // code: reqBatch?.barcodeNo,
          batchNumber: reqBatch?.batchNo,
          // { name: reqBatch?.batchNo, value: reqBatch?.batchId },
          manufacturingDate: reqBatch?.mfgDate,
          expiryDate: reqBatch?.expiryDate,
          location: {
            name: reqLocation?.locationName,
            value: reqLocation?.locationName,
            id: reqLocation?.id ?? reqBatch?.locationId,
          },
          purchaseUom: reqBatch?.purchaseUom ?? "",
          salesUom: reqBatch?.salesUom ?? "",
          conversionFactor: reqBatch?.conversionFactor,
          quantity: reqBatch?.quantity,
          subunits: reqBatch?.subunits,
          cgst: reqBatch?.cgst,
          sgst: reqBatch?.sgst,
          itemTotal: reqBatch?.itemTotal,
          unitSellingPrice: reqBatch?.unitSellingPrice,
          oldSellingPrice: reqBatch?.unitSellingPrice,
          // barcodeNo: reqBatch?.barcodeNo,
        };
        if (reqBatch?.batchId) reqObj.batchId = reqBatch?.batchId;
      }
    }
    if (name === "purchaseUom" && value?.value) {
      const reqList =
        editValues?.uomMapList?.length > 0
          ? editValues?.uomMapList?.map((li, i) => {
              if (i === 0) return { ...li, unit: value };
              return li;
            })
          : [{ unit: value, conversion: "1", allowedSale: true, i: 0 }];
      reqObj.uomMapList = reqList;
    }
    if (name === "category") {
      reqObj.subCategory = null;
    }
    setEditValues(reqObj);
    const reqErrs = {
      ...editErrs,
      [name]:
        reqValue?.length > 0
          ? false
          : (editValues?.billable
                ? requiredFields
                : requiredFieldsNonBillable
              )?.includes(name)
            ? true
            : false,
    };

    const reqHelps = {
      ...editHelps,
      [name]:
        reqValue?.length > 0
          ? ""
          : (editValues?.billable
                ? requiredFields
                : requiredFieldsNonBillable
              )?.includes(name)
            ? reqTxt
            : "",
    };
    if (name === "cgst" || name === "sgst") {
      if (reqValue?.length > 0) {
        reqHelps.cgst = "";
        reqHelps.sgst = "";
        reqErrs.cgst = false;
        reqErrs.sgst = false;
      } else {
        reqHelps.cgst = reqTxt;
        reqHelps.sgst = reqTxt;
        reqErrs.cgst = true;
        reqErrs.sgst = true;
      }
    }
    setEditHelps(reqHelps);
    setEditErrs(reqErrs);
  };

  const handleBatchChange = (name, value) => {
    let reqObj = { ...batchValues, [name]: value };
    const reqValue = typeof value === "object" ? value?.value : value;
    if (name === "quantity") {
      reqObj.subunits =
        Number(editValues?.conversionFactor ?? 1) * Number(value);
      reqObj.landedCost =
        Number(editValues?.itemTotal ?? 0) /
        (Number(editValues?.conversionFactor ?? 1) * Number(value ?? 0));
    }
    if (name === "unitSellingPrice") {
      reqObj.oldSellingPrice = value;
    }
    setBatchValues(reqObj);
    const reqErrs = {
      ...batchErrs,
      [name]:
        reqValue?.length > 0
          ? false
          : batchRequiredFields?.includes(name)
            ? true
            : false,
    };

    const reqHelps = {
      ...batchHelps,
      [name]:
        reqValue?.length > 0
          ? ""
          : batchRequiredFields?.includes(name)
            ? "This is required field"
            : "",
    };
    setBatchHelps(reqHelps);
    setBatchErrs(reqErrs);
  };

  const onChangeUomMap = (name, value, ind) => {
    const reqList = editValues?.uomMapList?.map((li) => {
      if (li?.i === ind) {
        const reqObj = { ...li, [name]: value };
        if (name === "unit") reqObj.unitErr = value?.value ? "" : reqTxt;
        if (name === "conversion") {
          reqObj.conErr =
            value?.length > 0
              ? Number(value) > 0
                ? ""
                : "Value must be positive"
              : reqTxt;
        }
        return reqObj;
      }
      return li;
    });
    setEditValues({ ...editValues, uomMapList: reqList });
  };

  const handleBatchEdit = (batch) => {
    const reqBatch = batch;
    const reqObj = {
      ...batchValues,
      batchId: reqBatch?.batchId ?? null,
      batchNumber: reqBatch?.batchNumber,
      manufacturingDate: reqBatch?.manufacturingDate,
      expiryDate: reqBatch?.expiryDate,
      location: batch?.location,
      quantity: reqBatch?.quantity,
      subunits: reqBatch?.subunits,
      unitSellingPrice: Number(reqBatch?.unitSellingPrice)?.toFixed(2),
      landedCost: Number(reqBatch?.landedCost)?.toFixed(2),
      pricingUOM: batch?.pricingUOM,
      quantityUOM: batch?.quantityUOM,
    };
    setBatchValues(reqObj);
    setEditValues({
      ...editValues,
      activeBatchIndex: batch?.i,
      isBatchEdit: true,
    });
  };

  const getPricingUomList = () => {
    const reqList = editValues?.uomMapList
      ?.filter(
        (li) =>
          !li?.delete &&
          (li?.uomMappingId ? li?.createdBy === clinic?.id : true),
      )
      // ?.filter((li) => li?.allowedSale)
      ?.map((li) => li?.unit)
      ?.filter(Boolean)
      ?.map((li) => {
        const reqObj = editValues?.uomMapList?.find((ml) =>
          matchStrIgnoreCase(ml?.unit?.value, li?.value),
        );
        return {
          name: reqObj?.unit?.name,
          value: reqObj?.unit?.value,
          uomMappingId: reqObj?.uomMappingId ?? null,
        };
      });
    return reqList ?? [];
  };

  const onDeleteUomByInd = (ind, mapId = null) => {
    const reqObj = { ...editValues };
    if (mapId) {
      const reqList = editValues?.uomMapList?.map((li) => {
        if (li?.i === ind) {
          return { ...li, delete: true };
        }
        return li;
      });
      reqObj.uomMapList = reqList;
    } else {
      reqObj.uomMapList = editValues?.uomMapList
        ?.filter((li) => li?.i !== ind)
        ?.map((li, i) => ({ ...li, i }));
    }
    setEditValues(reqObj);
  };

  const addNewUOMMap = () => {
    const reqList = [...(editValues?.uomMapList ?? [])];
    reqList.push({
      unit: null,
      conversion: "1",
      allowedSale: true,
      i: (editValues?.uomMapList ?? [])?.length ?? 0,
    });
    setEditValues({ ...editValues, uomMapList: reqList });
  };

  const handleBatchValidationCheck = () => {
    let hlp = {},
      err = {};
    [
      ...batchRequiredFields,
      ...(editValues?.billable ? ["unitSellingPrice", "pricingUOM"] : []),
    ]?.forEach((fld) => {
      const reqValue =
        typeof batchValues?.[fld] === "object"
          ? batchValues?.[fld]?.value
          : batchValues?.[fld];
      if (typeof reqValue === "number" ? false : !reqValue) {
        err = {
          ...err,
          [fld]:
            fld === "unitSellingPrice"
              ? editValues?.billable
                ? true
                : false
              : true,
        };
        hlp = {
          ...hlp,
          [fld]:
            fld === "unitSellingPrice"
              ? editValues?.billable
                ? "This is required field"
                : ""
              : "This is required field",
        };
      }
    });
    setBatchHelps(hlp);
    setBatchErrs(err);
    return Object.values(err).some((item) => item);
  };

  const handleSaveBatch = () => {
    if (handleBatchValidationCheck()) return;
    const reqBatchList = editValues?.batchList?.map((bat) => {
      if (bat?.i === editValues?.activeBatchIndex) {
        const reqObj = {
          ...bat,
          i: bat?.i,
          batchNumber: batchValues?.batchNumber,
          manufacturingDate: batchValues?.manufacturingDate,
          expiryDate: batchValues?.expiryDate,
          location: batchValues?.location,
          quantity: batchValues?.quantity,
          subunits: batchValues?.subunits,
          unitSellingPrice: editValues?.billable
            ? batchValues?.unitSellingPrice
            : 0,
          landedCost: batchValues?.landedCost,
          pricingUOM: editValues?.billable ? batchValues?.pricingUOM : null,
          quantityUOM: batchValues?.quantityUOM,
        };
        return reqObj;
      }
      return bat;
    });
    setBatchValues({});
    setBatchErrs({});
    setBatchHelps({});
    setEditValues({
      ...editValues,
      batchList: reqBatchList,
      activeBatchIndex: null,
      isBatchEdit: false,
    });
  };

  const onCloseBatchEdit = () => {
    let reqBatchList = editValues?.batchList;
    if (
      batchValues?.batchNumber === "" &&
      batchValues?.manufacturingDate === null &&
      batchValues?.expiryDate === null &&
      batchValues?.location === null &&
      batchValues?.quantity === "" &&
      batchValues?.subunits === "" &&
      batchValues?.unitSellingPrice === "" &&
      batchValues?.landedCost === ""
    ) {
      reqBatchList = reqBatchList
        ?.filter((bat) => bat?.i !== editValues?.activeBatchIndex)
        ?.map((li, i) => ({ ...li, i }));
    }
    setEditValues({
      ...editValues,
      batchList: reqBatchList,
      isBatchEdit: false,
      activeBatchIndex: null,
    });
  };

  const handleAddNewBatch = () => {
    const batchList = [
      ...(editValues?.batchList ?? []),
      { ...initBatch, i: editValues?.batchList?.length ?? 0 },
    ];
    setBatchValues(initBatch);
    setEditValues({
      ...editValues,
      isBatchEdit: true,
      batchList,
      activeBatchIndex: editValues?.batchList?.length ?? 0,
    });
  };

  const getLocationList = () => {
    if (locations?.length === 0 || !locations) return [];
    return locations
      ?.filter((loc) => isActiveRecord(loc))
      ?.map((loc) => ({
        name: loc?.locationName,
        value: loc?.locationName,
        id: loc?.id,
      }));
  };

  const handleValidationCheck = () => {
    let hlp = {},
      err = {};
    (editValues?.billable
      ? requiredFields
      : requiredFieldsNonBillable
    )?.forEach((fld) => {
      const reqValue =
        typeof editValues?.[fld] === "object"
          ? editValues?.[fld]?.value
          : editValues?.[fld];
      if (typeof reqValue === "number" ? false : !reqValue) {
        err = {
          ...err,
          [fld]:
            fld === "unitSellingPrice"
              ? editValues?.billable
                ? true
                : false
              : true,
        };
        hlp = {
          ...hlp,
          [fld]:
            fld === "unitSellingPrice"
              ? editValues?.billable
                ? reqTxt
                : ""
              : reqTxt,
        };
      }
    });
    setEditHelps(hlp);
    setEditErrs(err);
    return Object.values(err).some((item) => item);
  };

  const validateUOMMap = () => {
    const isNotValid = editValues?.uomMapList
      ?.filter(
        (li) =>
          !li?.delete &&
          (li?.uomMappingId ? li?.createdBy === clinic?.id : true),
      )
      ?.some((li) => !li?.unit?.value || !li?.conversion);
    if (isNotValid) {
      const reqList = editValues?.uomMapList?.map((li) => {
        if (!li?.unit?.value || !li?.conversion) {
          return {
            ...li,
            unitErr: !li?.unit?.value ? reqTxt : "",
            conErr:
              li?.conversion?.length > 0
                ? Number(li?.conversion) > 0
                  ? ""
                  : "Value must be positive"
                : reqTxt,
          };
        }
        return li;
      });
      setEditValues({ ...editValues, uomMapList: reqList });
      return true;
    }
    return false;
  };

  const handleSubmit = () => {
    if (
      handleValidationCheck() ||
      validateUOMMap() ||
      // editValues?.batchList?.length === 0 ||
      !editValues?.batchList ||
      typeof editValues?.activeBatchIndex === "number" ||
      editValues?.uomMapList?.filter((li) =>
        !li?.delete && li?.uomMappingId ? li?.createdBy === clinic?.id : true,
      )?.length === 0 ||
      !editValues?.uomMapList?.filter((li) =>
        !li?.delete && li?.uomMappingId ? li?.createdBy === clinic?.id : true,
      )
    ) {
      if (validateUOMMap()) {
        return alert("Plaese update the UOM map");
      }
      if (typeof editValues?.activeBatchIndex === "number") {
        return alert("Save or Update the current batch");
      }
      if (
        editValues?.uomMapList?.filter((li) =>
          !li?.delete && li?.uomMappingId ? li?.createdBy === clinic?.id : true,
        )?.length === 0 ||
        !editValues?.uomMapList?.filter((li) =>
          !li?.delete && li?.uomMappingId ? li?.createdBy === clinic?.id : true,
        )
      ) {
        return alert("Add atleast one UOM in the UOM Mapping table");
      }
      return;
    }
    const reqMapList = editValues?.uomMapList
      ?.filter(
        (ml) =>
          (!ml?.delete || ml?.uomMappingId) &&
          (ml?.createdBy ? ml?.createdBy === clinic?.id : true),
      )
      ?.map((ml) => {
        const newObj = {
          unit: ml?.unit?.value ?? null,
          conversion: Number(ml?.conversion ?? 1),
          allowSale: ml?.allowedSale ? "Y" : "N",
          delete: !!ml?.delete,
        };
        if (ml?.uomMappingId) {
          newObj.mapId = ml?.uomMappingId;
        }
        return newObj;
      });
    dispatch(showLoader());
    try {
      if (selecetdStock?.stockId) {
        // const subunits =
        //   Number(editValues?.quantity ?? 1) *
        //   Number(editValues?.conversionFactor ?? 1);
        const reqBatchList = editValues?.batchList?.map((bat) => {
          const reqObj = {
            batchId: bat?.batchId ?? null,
            packageUom: editValues?.adminUom?.value ?? "",
            // `${editValues?.packageUom}${editValues?.adminUom?.value}` ?? "",
            dosage: editValues?.dosage ?? "",
            dosageType: editValues?.dosageType?.value ?? null,
            purchaseUom: editValues?.purchaseUom?.value ?? "",
            salesUom: editValues?.salesUom?.value ?? "",
            conversionFactor: editValues?.conversionFactor ?? "1",
            subunits:
              Number(bat?.subunits) ??
              Number(bat?.quantity ?? 1) *
                Number(editValues?.conversionFactor ?? 1),
            quantity: Number(getExactNumber(bat?.quantity) ?? 1),
            // barCodeNo: editValues?.code ?? "",
            description: editValues?.description ?? "",
            batchNumber: bat?.batchNumber,
            brand: editValues?.brand ?? "",
            category: editValues?.category?.value,
            mfgDate: moment(
              new Date(bat?.manufacturingDate ?? new Date()),
            ).format("YYYY-MM-DD"),
            expiryDate: moment(new Date(bat?.expiryDate ?? new Date())).format(
              "YYYY-MM-DD",
            ),
            productName: String(editValues?.name ?? "").trim(),
            hsnSacCode: editValues?.hsnSacCode,
            uqcCode: editValues?.uqcCode ?? "",
            tradeName: String(editValues?.tradeName ?? "").trim(),
            ingredient: editValues?.ingredient ?? "",
            nature: editValues?.nature ?? "",
            type: "",
            taxes: 0,
            tax: 0,
            subCategory:
              editValues?.subCategory?.value ?? editValues?.subCategory ?? "",
            unitSellingPrice: Number(
              getExactNumber(bat?.unitSellingPrice) ?? 0,
            )?.toFixed(2),
            location: bat?.location?.id,
            landedCost: Number(bat?.landedCost ?? 0),
            cgst: Number(getExactNumber(editValues?.cgst) ?? 0),
            //  editValues?.billable
            //   ? Number(getExactNumber(editValues?.cgst) ?? 0)
            //   : 0,
            sgst: Number(getExactNumber(editValues?.sgst) ?? 0),
            // editValues?.billable
            //   ? Number(getExactNumber(editValues?.sgst) ?? 0)
            //   : 0,
            mapId: bat?.pricingUOM?.uomMappingId ?? null,
            uomMapName: bat?.pricingUOM?.value ?? "",
            qtyMapId: bat?.quantityUOM?.uomMappingId ?? null,
            qtyMapName: bat?.quantityUOM?.value ?? "",
            // batchNumber: bat?.batchNumber,
            // quantity: Number(getExactNumber(bat?.quantity) ?? 1),
            // landedCost:
            //   Number(
            //     Number(getExactNumber(editValues?.itemTotal) ?? 0) /
            //       Number(
            //         Number(bat?.quantity ?? 1) *
            //           Number(editValues?.conversionFactor ?? 1)
            //       ) ?? 1
            //   ) ?? Number(bat?.landedCost),
            // mfgDate: moment(
            //   new Date(bat?.manufacturingDate ?? new Date())
            // ).format("YYYY-MM-DD"),
            // expiryDate: moment(new Date(bat?.expiryDate ?? new Date())).format(
            //   "YYYY-MM-DD"
            // ),
            // location: bat?.location?.id,
            // subunits:
            //   Number(bat?.subunits) ??
            //   Number(bat?.quantity ?? 1) *
            //     Number(editValues?.conversionFactor ?? 1),
            // unitSellingPrice: Number(
            //   getExactNumber(bat?.unitSellingPrice) ?? 0
            // ),
          };
          return reqObj;
        });
        const reqSettingId =
          selecetdStock?.clinic_stock_settings?.find(
            (st) => st?.createdBy === clinic?.id,
          )?.stockSettingsId ?? null;
        const clinicStockSettings = [
          {
            stockSettingsId: reqSettingId,
            active: editValues?.active,
            billable: editValues?.billable,
            autoConsumed: editValues?.autoConsumed,
            reorderLevel: editValues?.reorderLevel ?? "",
            reorderQty: Number(editValues?.reorderQty ?? 0),
            thresholdUom: editValues?.thresholdUom?.value ?? null,
          },
        ];
        const data = {
          multipleBatch: reqBatchList,
          skuId: editValues?.skuId ?? "",
          active: editValues?.active,
          ndps: editValues?.ndps,
          autoConsumed: editValues?.autoConsumed,
          billable: editValues?.billable,
          ingredient: editValues?.ingredient ?? "",
          description: editValues?.description ?? "",
          brand: editValues?.brand ?? "",
          barCodeNo: editValues?.code ?? "",
          productName: editValues?.name ?? "",
          hsnSacCode: editValues?.hsnSacCode ?? "",
          uqcCode: editValues?.uqcCode ?? "",
          tradeName: String(editValues?.tradeName ?? "").trim(),
          shortName: editValues?.shortName ?? "",
          category: editValues?.category?.value,
          subCategory:
            editValues?.subCategory?.value ?? editValues?.subCategory ?? "",
          nature: editValues?.nature ?? "",
          packageUom: editValues?.adminUom?.value ?? "",
          // `${editValues?.packageUom}${editValues?.adminUom?.value}` ?? "",
          dosage: editValues?.dosage ?? "",
          dosageType: editValues?.dosageType?.value ?? null,
          purchaseUom: editValues?.purchaseUom?.value ?? "",
          salesUom: editValues?.salesUom?.value ?? "",
          // subunits,
          conversionFactor: editValues?.conversionFactor ?? "1",
          cgst: Number(getExactNumber(editValues?.cgst) ?? 0),
          // editValues?.billable
          //   ? Number(getExactNumber(editValues?.cgst) ?? 0)
          //   : 0,
          sgst: Number(getExactNumber(editValues?.sgst) ?? 0),
          //  editValues?.billable
          //   ? Number(getExactNumber(editValues?.sgst) ?? 0)
          //   : 0,
          itemTotal: Number(getExactNumber(editValues?.itemTotal) ?? 0),
          landedCost: Number(editValues?.batchList?.[0]?.landedCost ?? 0),
          drugSchedule: editValues?.drugSchedule?.value ?? "",
          status: true, //for stock table, if send false to go to the pending table
          groupName: editValues?.groupName ?? "",
          reorderLevel: editValues?.reorderLevel ?? "",
          reorderQty: Number(editValues?.reorderQty ?? 0),
          thresholdUomName: editValues?.thresholdUom?.value,
          thresholdUomId: editValues?.thresholdUom?.uomMappingId ?? null,
          clinicStockSettings,
        };
        dispatch(
          updateStockWithBatchList({ stockId: selecetdStock?.stockId, data }),
        ).then((res) => {
          if (res?.payload) {
            dispatch(
              addUpdateUomMappingByStockId({
                stockId: selecetdStock?.stockId,
                data: reqMapList,
              }),
            ).then((res2) => {
              if (res2?.payload) {
                onCloseEditMod("apiSuccess");
                dispatch(
                  getClinicStocksWoLoader(
                    `?inventryData=${dataSharing?.inventryData}`,
                  ),
                ).then((stcRes) => {
                  if (stcRes?.payload) {
                    patchMandateSession({
                      stocks: stcRes?.payload?.data ?? [],
                    });
                  }
                });
              }
            });
          }
        });
      } else {
        const reqBatchList = editValues?.batchList?.map((bat) => {
          const reqObj = {
            batchId: bat?.batchId ?? null,
            batchNumber: bat?.batchNumber,
            mfgDate: moment(
              new Date(bat?.manufacturingDate ?? new Date()),
            ).format("YYYY-MM-DD"),
            expiryDate: moment(new Date(bat?.expiryDate ?? new Date())).format(
              "YYYY-MM-DD",
            ),
            location: bat?.location?.id,
            subunits:
              Number(bat?.subunits) ??
              Number(bat?.quantity ?? 1) *
                Number(editValues?.conversionFactor ?? 1),
            quantity: Number(getExactNumber(bat?.quantity) ?? 1),
            mapId: bat?.pricingUOM?.uomMappingId ?? null,
            uomMapName: bat?.pricingUOM?.value ?? "",
            qtyMapId: bat?.quantityUOM?.uomMappingId ?? null,
            qtyMapName: bat?.quantityUOM?.value ?? "",
            unitSellingPrice: Number(
              getExactNumber(bat?.unitSellingPrice) ?? 0,
            )?.toFixed(2),
            landedCost: Number(bat?.landedCost ?? 0),
            cgst: Number(getExactNumber(editValues?.cgst) ?? 0),
            //  editValues?.billable
            //   ? Number(getExactNumber(editValues?.cgst) ?? 0)
            //   : 0,
            sgst: Number(getExactNumber(editValues?.sgst) ?? 0),
            // editValues?.billable
            //   ? Number(getExactNumber(editValues?.sgst) ?? 0)
            //   : 0,
          };
          return reqObj;
        });
        const stckObj = {
          skuId: editValues?.skuId ?? "",
          active: editValues?.active,
          ndps: editValues?.ndps,
          autoConsumed: editValues?.autoConsumed,
          billable: editValues?.billable,
          ingredient: editValues?.ingredient ?? "",
          description: editValues?.description ?? "",
          brand: editValues?.brand ?? "",
          barCodeNo: editValues?.code ?? "",
          productName: editValues?.name ?? "",
          hsnSacCode: editValues?.hsnSacCode ?? "",
          uqcCode: editValues?.uqcCode ?? "",
          tradeName: String(editValues?.tradeName ?? "").trim(),
          shortName: editValues?.shortName ?? "",
          category: editValues?.category?.value,
          subCategory:
            editValues?.subCategory?.value ?? editValues?.subCategory ?? "",
          nature: editValues?.nature ?? "",
          packageUom: editValues?.adminUom?.value ?? "",
          // `${editValues?.packageUom}${editValues?.adminUom?.value}` ?? "",
          dosage: editValues?.dosage ?? "",
          dosageType: editValues?.dosageType?.value ?? null,
          purchaseUom: editValues?.purchaseUom?.value ?? "",
          salesUom: editValues?.salesUom?.value ?? "",
          // subunits,
          conversionFactor: editValues?.conversionFactor ?? "1",
          cgst: Number(getExactNumber(editValues?.cgst) ?? 0),
          // editValues?.billable
          //   ? Number(getExactNumber(editValues?.cgst) ?? 0)
          //   : 0,
          sgst: Number(getExactNumber(editValues?.sgst) ?? 0),
          //  editValues?.billable
          //   ? Number(getExactNumber(editValues?.sgst) ?? 0)
          //   : 0,
          itemTotal: Number(getExactNumber(editValues?.itemTotal) ?? 0),
          landedCost: Number(editValues?.batchList?.[0]?.landedCost ?? 0),
          drugSchedule: editValues?.drugSchedule?.value ?? "",
          status: true,
          product: reqBatchList,
          groupName: editValues?.groupName ?? "",
          reorderLevel: editValues?.reorderLevel ?? "",
          reorderQty: Number(editValues?.reorderQty ?? 0),
          thresholdUomName: editValues?.thresholdUom?.value,
          thresholdUomId: editValues?.thresholdUom?.uomMappingId ?? null,
        };
        const reqObj = { stocks: stckObj };
        dispatch(createStocks(reqObj)).then((res) => {
          if (res?.payload) {
            dispatch(
              addUpdateUomMappingByStockId({
                stockId: res?.payload?.data,
                data: reqMapList,
              }),
            ).then((res2) => {
              if (res2?.payload) {
                onCloseEditMod("apiSuccess");
                dispatch(
                  getClinicStocksWoLoader(
                    `?inventryData=${dataSharing?.inventryData}`,
                  ),
                ).then((stcRes) => {
                  if (stcRes?.payload) {
                    patchMandateSession({
                      stocks: stcRes?.payload?.data ?? [],
                    });
                  }
                });
              }
            });
          }
        });
      }
    } catch (err) {
      dispatch(hideLoader());
    }
  };

  const onViewLedger = () => {
    if (!selecetdStock?.stockId) return;
    setLedgerModVsble(true);
  };

  const onCloseLedgerMod = () => {
    setLedgerModVsble(false);
  };

  return (
    <>
      <CustomModal
        open={modVsble}
        onClose={() => onCloseEditMod("close")}
        header={selecetdStock?.stockId ? "Edit" : "Create Stock"}
        headerCenter
        modalWidth={100}
        modal
      >
        <div className="ph20 scroll-80vh">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div className="flex-row">
                <Typography className="font-bold fs16 blue-color">
                  Item Master
                </Typography>
                {selecetdStock ? (
                  <div className="flex1-end">
                    <div>
                      <Typography className="gray-color fs12 font-medium">
                        Created Date:{" "}
                        {moment(
                          new Date(selecetdStock?.createdDate ?? new Date()),
                        ).format("DD MMM YYYY, HH:mm")}
                      </Typography>
                      <Typography className="gray-color fs12 font-medium">
                        Updated Date:{" "}
                        {moment(
                          new Date(
                            selecetdStock?.updatedDate ??
                              selecetdStock?.createdDate ??
                              new Date(),
                          ),
                        ).format("DD MMM YYYY, HH:mm")}
                      </Typography>
                      <Typography
                        className="blue-color fs12 font-medium underline cursor"
                        onClick={onViewLedger}
                      >
                        View Ledger
                      </Typography>
                    </div>
                  </div>
                ) : null}
              </div>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div className="flex-row-ali-cen">
                <div>
                  <CustomCheckbox
                    label={hl("NDPS Drug", "ndps")}
                    checked={editValues?.ndps}
                    onChange={() => handleChange("ndps", !editValues?.ndps)}
                  />
                </div>
              </div>
            </Grid>

            {/* <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <CustomCheckbox
                  label="Disabled"
                  checked={editValues?.active}
                  onChange={() => handleChange("active", !editValues?.active)}
                />
              </Grid> */}
            <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
              <div className="mb20">
                <CustomTextField
                  name="skuId"
                  label={hl("SKU ID", "skuId")}
                  // required={true}
                  fullWidth
                  value={editValues?.skuId}
                  // error={editErrs?.skuId}
                  // helperText={editHelps?.skuId}
                  handleChange={(e) => handleChange("skuId", e?.target?.value)}
                />
              </div>
            </Grid>
            <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
              <div className="mb20">
                <Select
                  list={stockCategories}
                  value={editValues?.category}
                  handleChange={(e, value) => handleChange("category", value)}
                  name="category"
                  search
                  error={editErrs?.category}
                  helperText={editHelps?.category}
                  label={hl("Category *", "category")}
                />
              </div>
            </Grid>
            <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
              <div className="mb20">
                <Select
                  list={subCategoryList}
                  value={editValues?.subCategory}
                  handleChange={(e, value) =>
                    handleChange("subCategory", value)
                  }
                  name="subCategory"
                  search
                  error={editErrs?.subCategory}
                  helperText={editHelps?.subCategory}
                  label={hl("Subcategory *", "subCategory")}
                />
              </div>
            </Grid>
            <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
              <div className="mb20">
                <CustomTextField
                  name="groupName"
                  label={hl("Groups", "groupName")}
                  // required={true}
                  fullWidth
                  value={editValues?.groupName}
                  error={editErrs?.groupName}
                  helperText={editHelps?.groupName}
                  handleChange={(e) =>
                    handleChange("groupName", e?.target?.value)
                  }
                />
              </div>
            </Grid>
            <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
              <div className="mb20">
                <CustomTextField
                  name="hsnSacCode"
                  fullWidth
                  value={editValues?.hsnSacCode}
                  error={editErrs?.hsnSacCode}
                  helperText={editHelps?.hsnSacCode}
                  label={hl("HSN Code", "hsnSacCode")}
                  handleChange={(e) =>
                    handleChange("hsnSacCode", e?.target?.value)
                  }
                />
              </div>
            </Grid>
            <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
              <div className="mb20">
                <CustomTextField
                  name="tradeName"
                  fullWidth
                  label={hl("Trade Name", "tradeName")}
                  required={true}
                  value={editValues?.tradeName}
                  error={editErrs?.tradeName}
                  helperText={editHelps?.tradeName}
                  handleChange={(e) =>
                    handleChange("tradeName", e?.target?.value)
                  }
                />
              </div>
            </Grid>
            <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
              <div className="mb20">
                <CustomTextField
                  name="name"
                  fullWidth
                  label={hl("Generic Name/Search Terms", "name")}
                  required={true}
                  value={editValues?.name}
                  error={editErrs?.name}
                  helperText={editHelps?.name}
                  handleChange={(e) => handleChange("name", e?.target?.value)}
                />
              </div>
            </Grid>
            <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
              <div className="mb20">
                <CustomTextField
                  name="shortName"
                  fullWidth
                  label={hl("Short Name", "shortName")}
                  value={editValues?.shortName}
                  error={editErrs?.shortName}
                  helperText={editHelps?.shortName}
                  handleChange={(e) =>
                    handleChange("shortName", e?.target?.value)
                  }
                />
              </div>
            </Grid>

            <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
              <div className="mb20">
                <CustomTextField
                  name="ingredient"
                  label={hl("Major Active Ingredients", "ingredient")}
                  fullWidth
                  value={editValues?.ingredient}
                  error={editErrs?.ingredient}
                  helperText={editHelps?.ingredient}
                  handleChange={(e) =>
                    handleChange("ingredient", e?.target?.value)
                  }
                />
              </div>
            </Grid>

            <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
              <div className="mb20">
                <CustomTextField
                  name="nature"
                  label={hl("Nature", "nature")}
                  fullWidth
                  value={editValues?.nature}
                  error={editErrs?.nature}
                  helperText={editHelps?.nature}
                  handleChange={(e) => handleChange("nature", e?.target?.value)}
                />
              </div>
            </Grid>

            <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
              <div className="mb20">
                <CustomTextField
                  name="code"
                  label={hl("Barcode Number", "code")}
                  fullWidth
                  value={editValues?.code}
                  error={editErrs?.code}
                  helperText={editHelps?.code}
                  handleChange={(e) => handleChange("code", e?.target?.value)}
                />
              </div>
            </Grid>

            <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
              <div className="mb20">
                <CustomTextField
                  name="brand"
                  label={hl("Brand", "brand")}
                  fullWidth
                  value={editValues?.brand}
                  error={editErrs?.brand}
                  helperText={editHelps?.brand}
                  handleChange={(e) => handleChange("brand", e?.target?.value)}
                />
              </div>
            </Grid>
            {drugScheduleShowCategoryList?.includes(
              editValues?.category?.value?.toLowerCase(),
            ) ? (
              <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
                <div className="mb20">
                  <Select
                    list={drugScheduleList}
                    value={editValues?.drugSchedule}
                    handleChange={(e, value) =>
                      handleChange("drugSchedule", value)
                    }
                    name="drugSchedule"
                    search
                    error={editErrs?.drugSchedule}
                    helperText={editHelps?.drugSchedule}
                    label={hl("Drug Schedule", "drugSchedule")}
                  />
                </div>
              </Grid>
            ) : null}

            {/* <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
                <div className="mb20">
                  <CustomTextField
                    name="purchaseUom"
                    label={hl("Purchase UOM", "purchaseUom")}
                    fullWidth
                    value={editValues?.purchaseUom}
                    error={editErrs?.purchaseUom}
                    helperText={editHelps?.purchaseUom}
                    handleChange={(e) =>
                      handleChange("purchaseUom", e?.target?.value)
                    }
                  />
                </div>
              </Grid> */}
            <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
              <div className="mb20">
                <Select
                  list={administeredUOMList}
                  value={editValues?.purchaseUom}
                  handleChange={(e, value) =>
                    handleChange("purchaseUom", value)
                  }
                  name="purchaseUom"
                  search
                  label={hl("Purchase UOM", "purchaseUom")}
                  // label="Base UOM"

                  // error={editErrs?.adminUom}
                  // helperText={editHelps?.adminUom}
                />
                {/* <CustomTextField
                    name="salesUom"
                    label="Base UOM"
                    fullWidth
                    value={editValues?.salesUom}
                    error={editErrs?.salesUom}
                    helperText={editHelps?.salesUom}
                    handleChange={(e) =>
                      handleChange("salesUom", e?.target?.value)
                    }
                  /> */}
              </div>
            </Grid>
            {/* <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
                <div className="mb20">
                  <CustomTextField
                    name="conversionFactor"
                    fullWidth
                    label="UOM Conversion Factor"
                    value={editValues?.conversionFactor}
                    error={editErrs?.conversionFactor}
                    helperText={editHelps?.conversionFactor}
                    handleChange={(e) =>
                      handleChange("conversionFactor", e?.target?.value)
                    }
                  />
                </div>
              </Grid> */}

            <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
              {/* <div className="flex-row">
                <div className="w60Per pr10">
                  <CustomTextField
                    name="packageUom"
                    label={hl("Administered UOM", "adminUom")}
                    fullWidth
                    value={editValues?.packageUom}
                    error={editErrs?.packageUom}
                    helperText={editHelps?.packageUom}
                    handleChange={(e) =>
                      handleChange("packageUom", e?.target?.value)
                    }
                    type="number"
                  />
                </div>
                <div className="w40Per"> */}
              <Select
                list={administeredUOMList}
                value={editValues?.adminUom}
                handleChange={(e, value) => handleChange("adminUom", value)}
                name="adminUom"
                search
                error={editErrs?.adminUom}
                helperText={editHelps?.adminUom}
                label={hl("Administered UOM", "adminUom")}
              />
              {/* </div>
              </div> */}
            </Grid>
            <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
              <div className="flex-row">
                <div className="w60Per pr10">
                  <CustomTextField
                    name="dosage"
                    label={hl("Dosage", "dosage")}
                    fullWidth
                    value={editValues?.dosage}
                    error={editErrs?.dosage}
                    helperText={editHelps?.dosage}
                    handleChange={(e) =>
                      handleChange("dosage", e?.target?.value)
                    }
                    // type="number"
                    // endIcon
                    // inputIcon={
                    //   <Custom3dotString
                    //     str={editValues?.adminUom?.name ?? ""}
                    //     length={9}
                    //     className="fs12 font-medium"
                    //   />
                    // }
                  />
                </div>
                <div className="w40Per">
                  <Select
                    list={dosageTypes}
                    value={editValues?.dosageType}
                    handleChange={(e, value) =>
                      handleChange("dosageType", value)
                    }
                    name="dosageType"
                    search
                    error={editErrs?.dosageType}
                    helperText={editHelps?.dosageType}
                    label={hl("Dosage Type", "dosageType")}
                  />
                </div>
              </div>
            </Grid>

            {/* {editValues?.billable && ( */}
            <>
              <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
                <div className="mb20">
                  <CustomTextField
                    name="cgst"
                    fullWidth
                    label={hl("CGST (%)", "cgst")}
                    required={true}
                    value={editValues?.cgst}
                    error={editErrs?.cgst}
                    helperText={editHelps?.cgst}
                    handleChange={(e) => handleChange("cgst", e?.target?.value)}
                    type="number"
                  />
                </div>
              </Grid>
              <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
                <div className="mb20">
                  <CustomTextField
                    name="sgst"
                    fullWidth
                    label={hl("SGST (%)", "sgst")}
                    required={true}
                    value={editValues?.sgst}
                    error={editErrs?.sgst}
                    helperText={editHelps?.sgst}
                    handleChange={(e) => handleChange("sgst", e?.target?.value)}
                    type="number"
                  />
                </div>
              </Grid>
              {/* <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
                    <div className="mb20">
                      <CustomTextField
                        name="itemTotal"
                        fullWidth
                        label=" Item Total (Buying) "
                        required={true}
                        value={editValues?.itemTotal}
                        error={editErrs?.itemTotal}
                        helperText={editHelps?.itemTotal}
                        handleChange={(e) =>
                          handleChange("itemTotal", e?.target?.value)
                        }
                        disabled={!isManualEntry}
                      />
                    </div>
                  </Grid> */}
            </>
            {/* )} */}

            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div className="mb20">
                <CustomTextField
                  name="description"
                  label={hl("Brief Description of the Product", "description")}
                  fullWidth
                  value={editValues?.description}
                  error={editErrs?.description}
                  helperText={editHelps?.description}
                  handleChange={(e) =>
                    handleChange("description", e?.target?.value)
                  }
                  multiline
                />
              </div>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <Typography className="font-bold fs16 blue-color">
                Operational Settings{" "}
                {dataSharing?.inventryData ? `(${clinic?.name})` : ""}
              </Typography>
              <div className="flex-row-ali-cen">
                <div>
                  <CustomCheckbox
                    label={hl("Active", "active")}
                    checked={editValues?.active}
                    onChange={() => handleChange("active", !editValues?.active)}
                  />
                </div>
                <div className="ml20">
                  <CustomCheckbox
                    label={hl("Billable", "billable")}
                    checked={editValues?.billable}
                    onChange={() =>
                      handleChange("billable", !editValues?.billable)
                    }
                  />
                </div>
                <div className="ml20">
                  <CustomCheckbox
                    label={hl("Auto-consumed", "autoConsumed")}
                    checked={editValues?.autoConsumed}
                    onChange={() =>
                      handleChange("autoConsumed", !editValues?.autoConsumed)
                    }
                  />
                </div>
              </div>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div className="flex-row-ali-cen">
                <Typography className="font-bold fs16 blue-color">
                  UOM Mapping (Product UOM)
                </Typography>
                <FieldInfoIcon text={STOCK_FIELD_HELP.uomMapping} />
              </div>
              <div className="flex-row">
                <div className="w25Per flex-row-ali-cen">
                  <Typography className="txt-semi-bold fs12 blue-color">
                    Conversion Factor
                  </Typography>
                  <FieldInfoIcon text={STOCK_FIELD_HELP.conversionFactor} />
                </div>
                <div className="w15Per flex-row-ali-cen">
                  <Typography className="txt-semi-bold fs12 blue-color">
                    UOM
                  </Typography>
                  <FieldInfoIcon text={STOCK_FIELD_HELP.salesUom} />
                </div>
                <div className="w10Per flex-row-ali-cen">
                  <Typography className="txt-semi-bold fs12 blue-color">
                    Allow Sale
                  </Typography>
                  <FieldInfoIcon text={STOCK_FIELD_HELP.allowedSale} />
                </div>
                <div className="w10Per" />
              </div>
              <div className="blue-hor-split mt5" />
              {editValues?.uomMapList
                ?.filter(
                  (ul) =>
                    !ul?.delete &&
                    (ul?.uomMappingId ? ul?.createdBy === clinic?.id : true),
                )
                ?.map((ul) => {
                  return (
                    <div className="flex-row mt10" key={ul?.i}>
                      <div className="w25Per pr10">
                        <CustomTextField
                          name="conversion"
                          fullWidth
                          value={ul?.conversion}
                          error={!!ul?.conErr}
                          helperText={ul?.conErr}
                          handleChange={(e) =>
                            onChangeUomMap(
                              "conversion",
                              e?.target?.value,
                              ul?.i,
                            )
                          }
                          type="number"
                          prefix={`1 ${
                            typeof editValues?.purchaseUom === "object" &&
                            editValues?.purchaseUom !== null
                              ? (editValues.purchaseUom?.name ??
                                editValues.purchaseUom?.value ??
                                "")
                              : (editValues?.purchaseUom ?? "")
                          } =`}
                        />
                      </div>
                      <div className="w15Per pr10">
                        <Select
                          list={administeredUOMList}
                          value={ul?.unit}
                          handleChange={(e, value) =>
                            onChangeUomMap("unit", value, ul?.i)
                          }
                          name="unit"
                          search
                          error={!!ul?.unitErr}
                          helperText={ul?.unitErr}
                        />
                      </div>

                      <div className="w10Per">
                        <CustomCheckbox
                          checked={ul?.allowedSale}
                          onChange={(e) =>
                            onChangeUomMap("allowedSale", !e, ul?.i)
                          }
                        />
                      </div>
                      <div className="w10Per">
                        {(
                          ul?.uomMappingId ? ul?.createdBy === clinic?.id : true
                        ) ? (
                          <div
                            className="cursor"
                            onClick={() =>
                              onDeleteUomByInd(ul?.i, ul?.uomMappingId ?? null)
                            }
                          >
                            <div className="flex-center">
                              <DeleteOutlineIcon sx={delBtn} />
                            </div>
                            <Typography className="red3 fs8 txt-semi-bold text-align-center">
                              Delete
                            </Typography>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              <div
                className="flex-row-ali-cen mt10 cursor"
                onClick={addNewUOMMap}
              >
                <div className="flex-center plus-icon">
                  <AddIcon sx={addIcon} />
                </div>
                <Typography className="blue-color fs16 ml5 font-bold">
                  Add
                </Typography>
              </div>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div className="flex-row">
                <div className="w25Per pr10">
                  <div className="flex-row">
                    <div className="w60Per pr10">
                      <CustomTextField
                        name="reorderLevel"
                        label={hl("Threshold Qty", "reorderLevel")}
                        fullWidth
                        value={editValues?.reorderLevel}
                        error={editErrs?.reorderLevel}
                        helperText={editHelps?.reorderLevel}
                        handleChange={(e) =>
                          handleChange("reorderLevel", e?.target?.value)
                        }
                        type="number"
                      />
                    </div>
                    <div className="w40Per">
                      <Select
                        list={getPricingUomList()}
                        value={editValues?.thresholdUom}
                        handleChange={(e, value) => {
                          handleChange("thresholdUom", value);
                        }}
                        name="thresholdUom"
                        search
                        error={editErrs?.thresholdUom}
                        helperText={editHelps?.thresholdUom}
                        label={hl("UOM", "thresholdUom")}
                      />
                    </div>
                  </div>
                </div>
                <div className="w15Per pr10">
                  <CustomTextField
                    name="reorderQty"
                    label={hl("Reorder Qty", "reorderQty")}
                    fullWidth
                    value={editValues?.reorderQty}
                    error={editErrs?.reorderQty}
                    helperText={editHelps?.reorderQty}
                    handleChange={(e) =>
                      handleChange("reorderQty", e?.target?.value)
                    }
                    type="number"
                  />
                </div>
              </div>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div className="flex-row-ali-cen">
                <Typography className="font-bold fs16 blue-color">
                  Batch Details
                </Typography>
                {editValues?.isBatchEdit ? (
                  <>
                    <div className="ml10">
                      <CustomButton
                        text="Save"
                        onClick={handleSaveBatch}
                        smallBtn
                      />
                    </div>
                    <div className="ml10">
                      <CustomButton
                        text="Discard"
                        onClick={onCloseBatchEdit}
                        redBtn
                        smallBtn
                      />
                    </div>
                  </>
                ) : (
                  <div
                    className="flex-row-ali-cen cursor ml10"
                    onClick={handleAddNewBatch}
                  >
                    <div className="flex-center plus-icon">
                      <AddIcon sx={addIcon} />
                    </div>
                    <Typography className="blue-color fs16 ml5 font-bold">
                      Add
                    </Typography>
                  </div>
                )}
              </div>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div className="flex-row scrollx scroll-hor">
                {!editValues?.isBatchEdit &&
                  editValues?.batchList?.length > 0 &&
                  (editValues?.batchList ?? [])
                    ?.filter((sb) => {
                      const userMatch = sb?.createdBy === clinic?.id; //profile?.id;
                      const matchedDataSharing =
                        branchesDataSharing?.find(
                          (li) => li?.createdBy === sb?.createdBy,
                        ) ?? {};
                      // const accessmatch = matchedDataSharing
                      //   ? (matchedDataSharing?.inventryData ?? false)
                      //   : false;
                      return (
                        (sb?.createdBy
                          ? userMatch // || accessmatch
                          : true) && (sb?.batchId ? sb?.quantity > 0 : true)
                      );
                    })
                    ?.map((sb, i) => (
                      <div
                        className={`blue-box-examD ${i !== 0 ? "ml20" : ""}`}
                        style={{ width: 400, flexShrink: 0 }}
                        key={sb?.batchId ?? i}
                      >
                        <div className="flex-row-ali-cen">
                          <Typography className="font-bold fs14 blue-color">
                            Batch Number :{" "}
                          </Typography>
                          <Typography className="txt-semi-bold fs14 black20 ml5">
                            {sb?.batchNumber ?? ""}
                          </Typography>
                          <div className="flex1-end">
                            <div
                              className="cursor"
                              onClick={() => handleBatchEdit(sb)}
                            >
                              <div className="flex-center">
                                <EditIcon sx={plusBtn} />
                              </div>
                              <Typography className="blue-color fs8 txt-semi-bold text-align-center">
                                Edit
                              </Typography>
                            </div>
                          </div>
                        </div>
                        <div className="flex-row">
                          <div className="w50Per">
                            <div className="flex-row-ali-cen">
                              <div className="gray-dot" />
                              <Typography className="font-medium fs12 blue-color ml5">
                                Mfg Date :{" "}
                              </Typography>
                              <Typography className="font-medium fs12 black20 ml5">
                                {sb?.manufacturingDate
                                  ? moment(
                                      new Date(sb?.manufacturingDate),
                                    ).format("YYYY-MM-DD")
                                  : null}
                              </Typography>
                            </div>
                            <div className="flex-row-ali-cen">
                              <div className="gray-dot" />
                              <Typography className="font-medium fs12 blue-color ml5">
                                Unit Qty :{" "}
                              </Typography>
                              <Typography className="font-medium fs12 black20 ml5">
                                {sb?.quantity} {sb?.quantityUOM?.value ?? ""}
                              </Typography>
                            </div>
                            <div className="flex-row-ali-cen">
                              <div className="gray-dot" />
                              <Typography className="font-medium fs12 blue-color ml5">
                                Selling Price :{" "}
                              </Typography>
                              <Typography className="font-medium fs12 black20 ml5">
                                {Number(
                                  getExactNumber(sb?.unitSellingPrice),
                                )?.toFixed(2)}{" "}
                                {sb?.pricingUOM?.value?.length > 0
                                  ? ` / ${sb?.pricingUOM?.value}`
                                  : ""}
                              </Typography>
                            </div>
                          </div>
                          <div className="w50Per">
                            <div className="flex-row-ali-cen">
                              <div className="gray-dot" />
                              <Typography className="font-medium fs12 blue-color ml5">
                                Expiry Date :{" "}
                              </Typography>
                              <Typography className="font-medium fs12 black20 ml5">
                                {sb?.expiryDate
                                  ? moment(new Date(sb?.expiryDate)).format(
                                      "YYYY-MM-DD",
                                    )
                                  : null}
                              </Typography>
                            </div>
                            {/* <div className="flex-row-ali-cen">
                              <div className="gray-dot" />
                              <Typography className="font-medium fs12 blue-color ml5">
                                Subunit Qty :{" "}
                              </Typography>
                              <Typography className="font-medium fs12 black20 ml5">
                                {sb?.subunits ?? ""}
                              </Typography>
                            </div> */}
                            <div className="flex-row-ali-cen">
                              <div className="gray-dot" />
                              <Typography className="font-medium fs12 blue-color ml5">
                                Landed Cost :{" "}
                              </Typography>
                              <Typography className="font-medium fs12 black20 ml5">
                                {Number(
                                  getExactNumber(sb?.landedCost),
                                )?.toFixed(2)}
                              </Typography>
                            </div>
                            {/* <div className="flex-row-ali-cen">
                            <div className="gray-dot" />
                            <Typography className="font-medium fs12 blue-color ml5">
                              Dosage :{" "}
                            </Typography>
                            <Typography className="font-medium fs12 black20 ml5">
                              {sb?.dosage ?? ""}
                            </Typography>
                          </div> */}
                            <div className="flex-row-ali-cen">
                              <div className="gray-dot" />
                              <Typography className="font-medium fs12 blue-color ml5">
                                Location :{" "}
                              </Typography>
                              <Typography className="font-medium fs12 black20 ml5">
                                {typeof sb?.location === "object" &&
                                sb?.location !== null
                                  ? (sb?.location?.name ??
                                    sb?.location?.value ??
                                    "")
                                  : (sb?.location ?? "")}
                              </Typography>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
              </div>
            </Grid>
            {editValues?.isBatchEdit && (
              <>
                <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
                  {/* {isManualEntry ? ( */}
                  <div className="mb20">
                    <CustomTextField
                      fullWidth
                      label={hl("Batch Number", "batchNumber")}
                      value={batchValues?.batchNumber}
                      handleChange={(e) =>
                        handleBatchChange("batchNumber", e?.target?.value)
                      }
                    />
                  </div>
                  {/* ) : (
                  <div className="mb20">
                    <Select
                      label={hl("Batch Number", "batchNumber")}
                      list={getBatchList(selecetdStock?.stockId)}
                      value={editValues?.batchNumber}
                      handleChange={(e) => {
                        handleChange("batchNumber", e?.value ?? e, {
                          batchId: e?.apiValue ?? null,
                        });
                      }}
                      addNewSelect
                      plusBtn
                    />
                  </div>
                )} */}
                </Grid>

                <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
                  <div className="mb20">
                    <CustomTextField
                      label={hl("Mfg Date", "manufacturingDate")}
                      fullWidth
                      value={batchValues?.manufacturingDate}
                      error={batchErrs?.manufacturingDate}
                      helperText={batchHelps?.manufacturingDate}
                      handleChange={(e) =>
                        handleBatchChange("manufacturingDate", e?.target?.value)
                      }
                      mobileDateWithBatch
                    />
                  </div>
                </Grid>

                <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
                  <div className="mb20">
                    <CustomTextField
                      fullWidth
                      label={hl("Expiry Date", "expiryDate")}
                      value={batchValues?.expiryDate}
                      error={batchErrs?.expiryDate}
                      helperText={batchHelps?.expiryDate}
                      handleChange={(e) =>
                        handleBatchChange("expiryDate", e?.target?.value)
                      }
                      mobileDateWithBatch
                    />
                  </div>
                </Grid>

                <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
                  <div className="mb20">
                    <Select
                      list={getLocationList()}
                      value={batchValues?.location}
                      handleChange={(e, value) =>
                        handleBatchChange("location", value)
                      }
                      name="location"
                      label={hl("Location *", "location")}
                      search
                      error={batchErrs?.location}
                      helperText={batchHelps?.location}
                    />
                  </div>
                </Grid>
                <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
                  <div className="mb20 flex-row">
                    <div className="w60Per pr10">
                      <CustomTextField
                        name="quantity"
                        label={hl("Unit Qty", "quantity")}
                        required={true}
                        fullWidth
                        value={batchValues?.quantity}
                        error={batchErrs?.quantity}
                        helperText={batchHelps?.quantity}
                        handleChange={(e) =>
                          handleBatchChange("quantity", e?.target?.value)
                        }
                        type="number"
                        disabled={batchValues?.batchId}
                      />
                    </div>
                    <div className="w40Per">
                      <Select
                        list={getPricingUomList()}
                        value={batchValues?.quantityUOM}
                        handleChange={(e, value) => {
                          handleBatchChange("quantityUOM", value);
                        }}
                        name="quantityUOM"
                        search
                        error={batchErrs?.quantityUOM}
                        helperText={batchHelps?.quantityUOM}
                        label={hl("UOM *", "quantityUOM")}
                        // disabled={batchValues?.batchId}
                      />
                    </div>
                  </div>
                </Grid>
                {/* <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
                    <div className="mb20">
                      <CustomTextField
                        name="subunits"
                        fullWidth
                        label="Subunit Qty"
                        required={true}
                        value={batchValues?.subunits}
                        error={batchErrs.subunits}
                        helperText={batchHelps.subunits}
                        handleChange={(e) =>
                          handleBatchChange("subunits", e?.target?.value)
                        }
                        disabled={!isManualEntry}
                        type="number"
                      />
                    </div>
                  </Grid> */}
                {editValues?.billable ? (
                  <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
                    <div className="mb20 flex-row">
                      <div className="w60Per pr10">
                        <CustomTextField
                          name="unitSellingPrice"
                          label={hl("Selling Price", "unitSellingPrice")}
                          required={true}
                          fullWidth
                          value={batchValues?.unitSellingPrice}
                          error={batchErrs?.unitSellingPrice}
                          helperText={batchHelps?.unitSellingPrice}
                          handleChange={(e) =>
                            handleBatchChange(
                              "unitSellingPrice",
                              e?.target?.value,
                            )
                          }
                          type="number"
                        />
                        {/* <div className="fs12 gray-color txt-regular mt5">
                          Price is per Unit UOM
                        </div> */}
                      </div>
                      <div className="w40Per">
                        <Select
                          list={getPricingUomList()}
                          value={batchValues?.pricingUOM}
                          handleChange={(e, value) => {
                            handleBatchChange("pricingUOM", value);
                          }}
                          name="pricingUOM"
                          search
                          error={batchErrs?.pricingUOM}
                          helperText={batchHelps?.pricingUOM}
                          label={hl("Pricing UOM *", "pricingUOM")}
                        />
                      </div>
                    </div>
                  </Grid>
                ) : null}
                <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
                  <div className="mb20">
                    <CustomTextField
                      name="landedCost"
                      fullWidth
                      label={hl("Landed Cost (Buying)", "landedCost")}
                      value={batchValues?.landedCost}
                      error={batchErrs?.landedCost}
                      helperText={batchHelps?.landedCost}
                      handleChange={(e) =>
                        handleBatchChange("landedCost", e?.target?.value)
                      }
                      disabled={selecetdStock?.stockId}
                    />
                  </div>
                </Grid>

                {/* {editValues?.billable ? (
                    <>
                      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Typography className="font-bold fs16 blue-color">
                          Price & Taxes
                        </Typography>
                      </Grid>
  
                      <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
                        <div className="mb20">
                          <CustomTextField
                            name="cgst"
                            fullWidth
                            label={hl("CGST (%)", "cgst")}
                            required={true}
                            value={editValues?.cgst}
                            error={editErrs?.cgst}
                            helperText={editHelps?.cgst}
                            handleChange={(e) =>
                              handleChange("cgst", e?.target?.value)
                            }
                            type="number"
                          />
                        </div>
                      </Grid>
                      <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
                        <div className="mb20">
                          <CustomTextField
                            name="sgst"
                            fullWidth
                            label={hl("SGST (%)", "sgst")}
                            required={true}
                            value={editValues?.sgst}
                            error={editErrs?.sgst}
                            helperText={editHelps?.sgst}
                            handleChange={(e) =>
                              handleChange("sgst", e?.target?.value)
                            }
                            type="number"
                          />
                        </div>
                      </Grid>
                      <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
                        <div className="mb20">
                          <CustomTextField
                            name="itemTotal"
                            fullWidth
                            label=" Item Total (Buying) "
                            required={true}
                            value={editValues?.itemTotal}
                            error={editErrs?.itemTotal}
                            helperText={editHelps?.itemTotal}
                            handleChange={(e) =>
                              handleChange("itemTotal", e?.target?.value)
                            }
                            disabled={!isManualEntry}
                          />
                        </div>
                      </Grid>
                    </>
                  ) : null} */}
              </>
            )}
          </Grid>

          {selecetdStock?.stockId ? (
            <div className="billing-change-log mt20 mb20">
              <div
                className="flex-row-ali-cen cursor mb10"
                onClick={handleToggleChangeLog}
              >
                <Typography className="font-bold fs14 black">
                  Change Log
                </Typography>
                {changeLogOpen ? (
                  <KeyboardArrowUpIcon
                    sx={{ fontSize: 20, color: AppColors.appPrimary, ml: 0.5 }}
                  />
                ) : (
                  <KeyboardArrowDownIcon
                    sx={{ fontSize: 20, color: AppColors.appPrimary, ml: 0.5 }}
                  />
                )}
              </div>
              <Collapse in={changeLogOpen} timeout="auto" unmountOnExit>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow style={tblGrayBack}>
                        <TableCell className="table-text-black">
                          <Typography className="fs12 font-bold">
                            Date and Time
                          </Typography>
                        </TableCell>
                        <TableCell className="table-text-black">
                          <Typography className="fs12 font-bold">
                            Action
                          </Typography>
                        </TableCell>
                        <TableCell className="table-text-black">
                          <Typography className="fs12 font-bold">
                            Changes
                          </Typography>
                        </TableCell>
                        <TableCell className="table-text-black">
                          <Typography className="fs12 font-bold">
                            Performed by
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {batchEditLogRows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className="table-text-black">
                            <Typography className="fs12 gray4 flex-center pv10">
                              No change log available
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        batchEditLogRows.map((row) => (
                          <TableRow key={row.key}>
                            <TableCell className="table-text-black">
                              <Typography className="fs12">
                                {row.createdDate
                                  ? moment(row.createdDate).format(
                                      "DD MMM YYYY, hh:mm A",
                                    )
                                  : "-"}
                              </Typography>
                            </TableCell>
                            <TableCell className="table-text-black">
                              <Typography
                                className={`fs12 font-medium ${
                                  row.action === "Deleted"
                                    ? "red-color"
                                    : row.action === "Updated" ||
                                        String(row.action)
                                          .toLowerCase()
                                          .includes("update")
                                      ? "blue-color"
                                      : row.action === "Opening"
                                        ? "orange-color"
                                        : row.action === "Closing"
                                          ? "purple"
                                          : "green-color"
                                }`}
                              >
                                {row.action}
                              </Typography>
                            </TableCell>
                            <TableCell className="table-text-black">
                              <Typography className="fs12">
                                {row.changes}
                              </Typography>
                            </TableCell>
                            <TableCell className="table-text-black">
                              <Typography className="fs12">
                                {row.performedBy}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Collapse>
            </div>
          ) : null}

          <div className="mt30">
            <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
              <div className="flex1-center">
                <div className="flex-row-ali-cen">
                  <div className="ml10">
                    <CustomButton
                      text="Submit"
                      onClick={handleSubmit}
                      smallBtn
                    />
                  </div>
                </div>
              </div>
            </Grid>
          </div>
        </div>
      </CustomModal>
      <CustomModal
        open={ledgerModVsble}
        onClose={onCloseLedgerMod}
        header="Stock Transaction History"
        modal
        modalWidth={95}
      >
        <div className="scroll-90vh">
          <TransactionHistory
            key={selecetdStock?.stockId}
            selectedStock={{
              stockId: selecetdStock?.stockId,
            }}
            initialStartDate={
              new Date(new Date().getFullYear(), new Date().getMonth(), 1)
            }
            initialEndDate={new Date()}
            initType="all"
          />
        </div>
      </CustomModal>
    </>
  );
};

export default StockCreateUpdate;
